import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { SITE } from "../lib/config";
import Breadcrumb from "../components/site/Breadcrumb";

const inputCls =
  "w-full bg-white border border-[#E2E8E4] rounded-xl px-4 py-3 text-sm text-[#0A1C0E] outline-none focus:border-[#1D6B2A] focus:ring-4 focus:ring-[#4CAF50]/15 transition-all placeholder:text-[#3A4D3E]/50";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.createEnquiry(form);
      toast.success(res.message);
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="bg-[#F8FAF8] border-b border-[#E2E8E4] pt-12 pb-14">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Breadcrumb items={[{ label: "Contact" }]} />
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mt-6 mb-3">Get in touch</p>
          <h1 className="font-head text-4xl md:text-6xl font-medium tracking-tighter text-[#0A1C0E]">
            Contact Chennai Packs
          </h1>
          <p className="text-lg text-[#3A4D3E] mt-3 max-w-xl">
            Questions, bulk orders or custom branding? Our team is ready to help.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 space-y-4">
            {[
              { icon: MapPin, t: "Visit us", d: SITE.addressLines.join(" ") },
              { icon: Phone, t: "Call us", d: SITE.phone, href: `tel:${SITE.phoneRaw}` },
              { icon: MessageCircle, t: "WhatsApp us", d: "+91 8870705520", href: `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi Chennai Packs, I'd like a quote for your packaging products.")}`, external: true },
              { icon: Mail, t: "Email us", d: SITE.email, href: `mailto:${SITE.email}` },
            ].map((c) => (
              <div key={c.t} className="bg-white border border-[#E2E8E4] rounded-2xl p-6 flex gap-4">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#F8FAF8] text-[#1D6B2A] shrink-0">
                  <c.icon size={22} />
                </span>
                <div>
                  <h3 className="font-head font-semibold text-[#0A1C0E]">{c.t}</h3>
                  {c.href ? (
                    <a
                      href={c.href}
                      {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className="text-[#3A4D3E] hover:text-[#1D6B2A] text-sm"
                    >
                      {c.d}
                    </a>
                  ) : (
                    <p className="text-[#3A4D3E] text-sm">{c.d}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-7 bg-white border border-[#E2E8E4] rounded-3xl p-6 md:p-8 shadow-sm">
            {done ? (
              <div className="text-center py-12" data-testid="contact-success">
                <CheckCircle2 size={56} className="mx-auto text-[#1D6B2A] mb-4" />
                <h3 className="font-head text-2xl font-semibold text-[#0A1C0E]">Message sent!</h3>
                <p className="text-[#3A4D3E] mt-2">We'll get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name" />
                  <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email" />
                  <input placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone" />
                  <input placeholder="Subject" value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject" />
                </div>
                <textarea required rows={5} placeholder="Your message *" value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-message" />
                <button type="submit" disabled={loading} data-testid="contact-submit"
                  className="w-full bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {loading && <Loader2 size={18} className="animate-spin" />} Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="rounded-3xl overflow-hidden border border-[#E2E8E4] h-[420px]" data-testid="google-map">
            <iframe
              title="Chennai Packs location"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${encodeURIComponent(SITE.mapQuery)}&output=embed`}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
